import React, { useState, useCallback } from "react";
import { Zap, AlertTriangle, CheckCircle2, Circle, Search, XCircle, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { AdminFrame, useAdminData } from "./LiveData";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  error:      { label: "Error",      color: "text-red-500",    bg: "bg-red-50 dark:bg-red-950/30",    icon: XCircle },
  warning:    { label: "Stale",      color: "text-amber-500",  bg: "bg-amber-50 dark:bg-amber-950/30", icon: AlertTriangle },
  ok:         { label: "Healthy",    color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-950/30", icon: CheckCircle2 },
  no_sources: { label: "No sources", color: "text-[var(--muted)]", bg: "bg-[var(--surface-2)]",        icon: Circle },
};

const timeAgo = (iso) => {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 120)  return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const StoriesBadge = ({ n }) => {
  const cls = n >= 10 ? "text-emerald-600 bg-emerald-50" : n >= 1 ? "text-amber-600 bg-amber-50" : "text-red-500 bg-red-50";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
      {n}
    </span>
  );
};

const TriggerButton = ({ countrySlug, countryName, onDone }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/admin/feed-health/trigger`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country_slug: countrySlug }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Fetch triggered for ${countryName}`);
      onDone();
    } catch {
      toast.error("Could not trigger fetch");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      data-testid={`trigger-fetch-${countrySlug}`}
      title={`Trigger RSS fetch for ${countryName}`}
      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-[var(--hairline)] text-[var(--muted)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] disabled:opacity-40 transition-colors"
    >
      <Zap size={11} className={busy ? "animate-pulse" : ""} />
      {busy ? "Running…" : "Fetch"}
    </button>
  );
};

const SummaryCard = ({ label, value, sub, testId }) => (
  <div className="border border-[var(--hairline)] rounded-xl p-5" data-testid={testId}>
    <p className="text-[11px] font-mono uppercase text-[var(--muted)] tracking-wide">{label}</p>
    <p className="font-serif text-3xl mt-2">{value ?? "—"}</p>
    {sub && <p className="text-[11px] text-[var(--muted)] mt-1">{sub}</p>}
  </div>
);

const FILTERS = ["All", "Needs attention", "Healthy", "No sources"];

const ErrorSourceRow = ({ src, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const handleDelete = async () => {
    if (!window.confirm(`Remove "${src.name}" from all sources? This cannot be undone from this view.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/admin/sources/${src.source_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      toast.success(`"${src.name}" removed`);
      onDeleted(src.source_id);
    } catch {
      toast.error("Could not remove source");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 px-4 py-2 text-[11.5px] border-t border-[var(--hairline)] bg-red-50/60 dark:bg-red-950/20"
         data-testid={`fh-err-src-${src.source_id}`}>
      <span className="flex-1 font-medium text-[var(--ink)] truncate">{src.name}</span>
      <span className="shrink-0 tabular-nums text-red-600 font-semibold" title="Total failed fetches">
        {src.fail_count} {src.fail_count === 1 ? "fail" : "fails"}
      </span>
      <span className="shrink-0 text-[var(--muted)] font-mono bg-[var(--surface-2)] px-1.5 py-0.5 rounded text-[10px]">
        {src.error_type}
      </span>
      <button
        onClick={handleDelete}
        disabled={busy}
        data-testid={`fh-delete-src-${src.source_id}`}
        title="Remove this source"
        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-red-200 text-red-500 hover:bg-red-100 hover:border-red-400 disabled:opacity-40 transition-colors"
      >
        <Trash2 size={11} className={busy ? "animate-pulse" : ""} />
        {busy ? "Removing…" : "Remove"}
      </button>
    </div>
  );
};

export default function FeedHealth() {
  const s = useAdminData("feed-health");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [expanded, setExpanded] = useState({});
  const [removed, setRemoved] = useState({});   // source_ids removed this session

  const toggleExpand = (slug) => setExpanded(p => ({ ...p, [slug]: !p[slug] }));
  const handleDeleted = (sourceId) => setRemoved(p => ({ ...p, [sourceId]: true }));

  const triggerAll = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/admin/feed-health/trigger`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error();
      toast.success("Global fetch triggered — this may take a few minutes");
    } catch {
      toast.error("Could not trigger global fetch");
    }
  }, []);

  return (
    <AdminFrame id="feed-health" state={s}>
      {s.data && (() => {
        const { countries = [], summary = {}, as_of } = s.data;

        const filtered = countries.filter(c => {
          const matchQ = !query || c.country_name.toLowerCase().includes(query.toLowerCase());
          const matchF =
            filter === "All" ? true :
            filter === "Needs attention" ? (c.status === "error" || c.status === "warning") :
            filter === "Healthy" ? c.status === "ok" :
            c.status === "no_sources";
          return matchQ && matchF;
        });

        return (
          <div data-testid="feed-health-tab">
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <SummaryCard label="Active sources"     value={summary.total_sources}       testId="fh-total-sources" />
              <SummaryCard label="Erroring sources"   value={summary.erroring_sources}    testId="fh-erroring-sources" sub={summary.erroring_sources > 0 ? "requires attention" : "all healthy"} />
              <SummaryCard label="Countries with news" value={summary.countries_with_news} testId="fh-countries-news" sub="in the last 48 h" />
              <SummaryCard label="Countries no news"  value={summary.countries_no_news}   testId="fh-countries-no-news" sub="despite active sources" />
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {/* Filter tabs */}
              <div className="flex gap-1 flex-wrap">
                {FILTERS.map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`fh-filter-${f.toLowerCase().replace(/ /g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] border transition-colors ${
                      filter === f
                        ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]"
                        : "border-[var(--hairline)] text-[var(--muted)] hover:border-[var(--ink)]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="flex-1 min-w-[180px] relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                <input
                  type="search"
                  placeholder="Search country…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  data-testid="fh-search"
                  className="w-full h-8 pl-8 pr-3 text-[12px] rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                />
              </div>

              {/* Trigger all */}
              <button
                onClick={triggerAll}
                data-testid="fh-trigger-all"
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg border border-[var(--hairline)] text-[12px] text-[var(--muted)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors"
              >
                <Zap size={13} /> Trigger all
              </button>
            </div>

            {/* As-of timestamp */}
            {as_of && (
              <p className="text-[11px] text-[var(--muted)] mb-4">
                As of {new Date(as_of).toLocaleString()} · {filtered.length} countries shown
              </p>
            )}

            {/* Table */}
            <div className="border border-[var(--hairline)] rounded-xl overflow-hidden" data-testid="feed-health-table">
              {/* Header */}
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-x-4 px-4 py-2.5 bg-[var(--surface-2)] border-b border-[var(--hairline)]">
                {["Country", "Sources", "Stories 48h", "Last fetch", "Status", ""].map(h => (
                  <p key={h} className="text-[10px] font-mono uppercase text-[var(--muted)] tracking-wide">{h}</p>
                ))}
              </div>

              {/* Rows */}
              {filtered.length === 0 ? (
                <p className="text-[13px] text-[var(--muted)] py-12 text-center">No countries match this filter</p>
              ) : (
                filtered.map(c => {
                  const meta = STATUS_META[c.status] || STATUS_META.ok;
                  const Icon = meta.icon;
                  const visibleErrSources = (c.erroring_source_list || []).filter(src => !removed[src.source_id]);
                  const isExpanded = expanded[c.country_slug];
                  const hasErrors = visibleErrSources.length > 0;
                  return (
                    <div key={c.country_slug} data-testid={`fh-row-${c.country_slug}`}>
                      <div
                        className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-x-4 px-4 py-3 border-b border-[var(--hairline)] items-center text-[12.5px] ${meta.bg}`}
                      >
                        {/* Country */}
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-base leading-none shrink-0">{c.flag_emoji}</span>
                          <span className="font-medium text-[var(--ink)] truncate">{c.country_name}</span>
                        </div>

                        {/* Sources — clickable badge if errors */}
                        <div className="flex items-center gap-1">
                          <span className="text-[var(--ink-2)]">{c.active_sources}</span>
                          {hasErrors && (
                            <button
                              onClick={() => toggleExpand(c.country_slug)}
                              data-testid={`fh-expand-${c.country_slug}`}
                              className="flex items-center gap-0.5 text-red-500 text-[11px] hover:text-red-700 transition-colors"
                              title="Show failing sources"
                            >
                              {isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                              ({visibleErrSources.length} err)
                            </button>
                          )}
                          {!hasErrors && c.erroring_sources > 0 && (
                            <span className="ml-1 text-[var(--muted)] text-[11px] line-through">({c.erroring_sources} removed)</span>
                          )}
                        </div>

                        {/* Stories 48h */}
                        <StoriesBadge n={c.stories_48h} />

                        {/* Last fetch */}
                        <span className="text-[var(--muted)] text-[11.5px]">{timeAgo(c.last_ingested_at)}</span>

                        {/* Status */}
                        <span className={`flex items-center gap-1 text-[11px] font-medium ${meta.color}`}>
                          <Icon size={12} strokeWidth={2} />
                          {meta.label}
                        </span>

                        {/* Trigger */}
                        {c.active_sources > 0 ? (
                          <TriggerButton
                            countrySlug={c.country_slug}
                            countryName={c.country_name}
                            onDone={s.refresh}
                          />
                        ) : (
                          <span />
                        )}
                      </div>

                      {/* Expanded: erroring source list */}
                      {isExpanded && hasErrors && (
                        <div className="border-b border-[var(--hairline)]">
                          <div className="px-4 py-1.5 bg-red-50/40 dark:bg-red-950/10 flex items-center gap-2">
                            <span className="text-[10px] font-mono uppercase text-[var(--muted)] tracking-wide">Failing sources</span>
                            <span className="text-[10px] text-[var(--muted)]">— remove to stop wasting fetch quota</span>
                          </div>
                          {visibleErrSources.map(src => (
                            <ErrorSourceRow key={src.source_id} src={src} onDeleted={handleDeleted} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })()}
    </AdminFrame>
  );
}
